import { useEffect, useRef } from "preact/hooks"
import { useLocation } from "wouter-preact"
import { useSearch } from "wouter-preact/use-location"
import { isSameDay, parseISO } from "date-fns"
import { GlobalFilterState } from "@/global_filter/index"
import {
  global_filter_url_sync_enabled,
  has_global_filter_url_params,
  merge_global_filter_into_search,
  parse_global_filter_url_params,
  type GlobalFilterUrlFields,
} from "@/global_filter/url_sync.ts"

function normalize_search(search: string): string {
  if (!search) {
    return ""
  }
  return search.startsWith("?") ? search : `?${search}`
}

function global_filter_diff_from_url(
  filters: GlobalFilterUrlFields,
  search: string
): Partial<GlobalFilterUrlFields> {
  const parsed = parse_global_filter_url_params(search)
  const diff: Partial<GlobalFilterUrlFields> = {}

  if (
    parsed.startDate &&
    (!filters.startDate || !isSameDay(parsed.startDate, filters.startDate))
  ) {
    diff.startDate = parsed.startDate
  }
  if (parsed.endDate && (!filters.endDate || !isSameDay(parsed.endDate, filters.endDate))) {
    diff.endDate = parsed.endDate
  }
  if (parsed.dateKind && parsed.dateKind !== filters.dateKind) {
    diff.dateKind = parsed.dateKind
  }

  return diff
}

/** Sync global date filters with URL query params on deductions/transactions routes. */
export function useGlobalFilterUrlSync() {
  const [pathname] = useLocation()
  const search = normalize_search(useSearch())
  const { startDate, endDate, dateKind } = GlobalFilterState.use()
  const last_written_search = useRef<string | null>(null)
  const last_seen_search = useRef<string | null>(null)

  // URL -> state. Only reacts to genuine URL changes (navigation, shared links) — NOT to our own
  // filter edits. Keying this effect off `search` alone (rather than the filter values) prevents a
  // feedback loop where clicking a filter reverts it: on a state edit the URL has not been rewritten
  // yet, so re-reading it here would diff back to the stale value and undo the selection.
  useEffect(() => {
    const search_changed = search !== last_seen_search.current
    last_seen_search.current = search

    if (!global_filter_url_sync_enabled(pathname)) {
      return
    }
    // This render was triggered by a state edit, not a URL change: nothing to pull in.
    if (!search_changed) {
      return
    }
    // Echo of a URL we just wrote ourselves in the state -> URL effect.
    if (search === last_written_search.current) {
      return
    }
    if (!has_global_filter_url_params(search)) {
      return
    }

    const diff = global_filter_diff_from_url({ startDate, endDate, dateKind }, search)
    if (Object.keys(diff).length === 0) {
      return
    }

    GlobalFilterState.set(state => ({
      ...state,
      ...diff,
    }))
  }, [pathname, search])

  // state -> URL
  useEffect(() => {
    if (!global_filter_url_sync_enabled(pathname)) {
      return
    }

    const next_search = merge_global_filter_into_search(search, {
      startDate,
      endDate,
      dateKind,
    })
    const next_url = `${pathname}${next_search}`
    const current_url = `${pathname}${search}`
    if (next_url === current_url) {
      return
    }

    last_written_search.current = next_search
    last_seen_search.current = next_search
    window.history.replaceState({}, "", next_url)
  }, [pathname, search, startDate, endDate, dateKind])
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("global_filter_diff_from_url", () => {
    it("returns only changed fields", () => {
      const diff = global_filter_diff_from_url(
        {
          startDate: parseISO("2025-01-01"),
          endDate: parseISO("2025-06-30"),
          dateKind: "check",
        },
        "?start_date=2025-01-01&end_date=2025-06-30&date_kind=posting"
      )
      expect(diff).toEqual({ dateKind: "posting" })
    })

    it("returns empty when URL matches state", () => {
      const diff = global_filter_diff_from_url(
        {
          startDate: parseISO("2025-01-01"),
          endDate: parseISO("2025-06-30"),
          dateKind: "invoice",
        },
        "?start_date=2025-01-01&end_date=2025-06-30&date_kind=invoice"
      )
      expect(diff).toEqual({})
    })
  })
}
